import { useState, useEffect } from 'react';
import { ArrowLeft, Mail, Lock, CheckCircle, Check } from 'lucide-react';
import { supabase } from '../utils/supabase/client';
import { projectId, API_PUBLIC_BASE } from '../utils/supabase/info';
import { devLog, devError } from '../utils/logger';


interface ForgotPasswordPageProps {
  onBack: () => void;
  onLoginRedirect: () => void;
}

export function ForgotPasswordPage({ onBack, onLoginRedirect }: ForgotPasswordPageProps) {
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState<'request' | 'reset' | 'success'>('request');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [recoveryToken, setRecoveryToken] = useState<string | null>(null);

  // Scroll to top when component mounts or step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // Check if user came from password reset link
  useEffect(() => {
    const checkResetToken = async () => {
      let hash = window.location.hash.substring(1); // Remove first #
      
      devLog('🔍 Parsing password reset hash:', hash);
      
      // Handle different hash formats:
      // 1. #reset-password?token=XXX (standard format from backend)
      // 2. #reset-password#token=XXX (double hash)
      // 3. #token=XXX (direct token)
      
      let paramString = '';
      
      if (hash.includes('?')) {
        // Format: reset-password?token=XXX
        const questionIndex = hash.indexOf('?');
        paramString = hash.substring(questionIndex + 1);
      } else if (hash.includes('#')) {
        // Format: reset-password#token=XXX
        const secondHashIndex = hash.indexOf('#');
        paramString = hash.substring(secondHashIndex + 1);
      } else if (hash.includes('=')) {
        // Format: token=XXX (direct)
        paramString = hash;
      } else {
        devLog('❌ No parameters found in hash');
        return;
      }
      
      devLog('📋 Parsing param string:', paramString);
      
      const params = new URLSearchParams(paramString);
      const token = params.get('token');
      
      devLog('📋 Parsed params:', {
        hasToken: !!token,
        token: token ? `${token.substring(0, 10)}...` : null
      });
      
      if (token) {
        devLog('✅ Password reset token detected');
        setRecoveryToken(token);
        setStep('reset');
        // Clean up URL
        window.history.replaceState(null, '', window.location.pathname + '#reset-password');
      } else {
        devLog('❌ No token found in params');
      }
    };
    checkResetToken();
  }, []);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      // Call backend endpoint instead of Supabase
      const response = await fetch(
        `${API_PUBLIC_BASE}/request-password-reset`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Kunne ikke sende tilbakestillings-e-post');
      } else {
        setMessage('Sjekk e-posten din! Vi har sendt deg en lenke for å tilbakestille passordet.');
      }
    } catch (err) {
      setError('Kunne ikke sende tilbakestillings-e-post');
      devError('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passordene matcher ikke');
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('Passordet må være minst 8 tegn');
      setLoading(false);
      return;
    }

    // Enhanced password validation
    if (!/[A-Z]/.test(newPassword)) {
      setError('Passord må inneholde minst én stor bokstav');
      setLoading(false);
      return;
    }
    
    if (!/[a-z]/.test(newPassword)) {
      setError('Passord må inneholde minst én liten bokstav');
      setLoading(false);
      return;
    }
    
    if (!/[0-9]/.test(newPassword)) {
      setError('Passord må inneholde minst ett tall');
      setLoading(false);
      return;
    }
    
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(newPassword)) {
      setError('Passord må inneholde minst ett spesialtegn (!@#$%^&* osv.)');
      setLoading(false);
      return;
    }

    try {
      if (!recoveryToken) {
        setError('Ugyldig tilbakestillingslenke. Vennligst be om en ny.');
        setLoading(false);
        return;
      }

      // Call backend to verify token and reset password
      const response = await fetch(
        `${API_PUBLIC_BASE}/verify-and-reset-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            token: recoveryToken,
            newPassword 
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Kunne ikke tilbakestille passord');
        setLoading(false);
        return;
      }

      // Password reset successful - no need to set session, user will log in manually
      setStep('success');
    } catch (err) {
      setError('Kunne ikke tilbakestille passord');
      devError('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen px-4 pt-6 pb-12">
      <div className="max-w-md w-full mx-auto">
        {/* Back button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Tilbake til innlogging</span>
        </button>

        <div className="bg-white rounded-3xl shadow-lg p-8 border-2 border-purple-100">
          {step === 'request' && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl sm:text-3xl mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                  Glemt passord?
                </h2>
                <p className="text-gray-600">
                  Ingen problem! Skriv inn e-postadressen din, så sender vi deg en lenke for å tilbakestille passordet.
                </p>
              </div>

              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <label className="block text-gray-700 mb-2">E-postadresse</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="din@epost.no"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-3 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                {message && (
                  <div className="bg-green-50 border-2 border-green-200 rounded-xl p-3 text-green-700 text-sm">
                    {message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50"
                >
                  {loading ? 'Sender...' : 'Send tilbakestillingslenke'}
                </button>
              </form>
            </>
          )}

          {step === 'reset' && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl sm:text-3xl mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                  Tilbakestill passord
                </h2>
                <p className="text-gray-600">
                  Skriv inn ditt nye passord.
                </p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-gray-700 mb-2">Nytt passord</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="••••••••"
                    required
                    minLength={8}
                  />
                  <div className="text-xs text-gray-500 mt-2 space-y-1 bg-purple-50 p-3 rounded-lg border border-purple-100">
                    <p className="font-semibold text-gray-700 mb-1">Passordkrav:</p>
                    <p className={`flex items-center gap-1 ${newPassword.length >= 8 ? 'text-green-600' : ''}`}>
                      <Check className="w-3 h-3" />
                      Minst 8 tegn
                    </p>
                    <p className={`flex items-center gap-1 ${/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}`}>
                      <Check className="w-3 h-3" />
                      Minst én stor bokstav (A-Z)
                    </p>
                    <p className={`flex items-center gap-1 ${/[a-z]/.test(newPassword) ? 'text-green-600' : ''}`}>
                      <Check className="w-3 h-3" />
                      Minst én liten bokstav (a-z)
                    </p>
                    <p className={`flex items-center gap-1 ${/[0-9]/.test(newPassword) ? 'text-green-600' : ''}`}>
                      <Check className="w-3 h-3" />
                      Minst ett tall (0-9)
                    </p>
                    <p className={`flex items-center gap-1 ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword) ? 'text-green-600' : ''}`}>
                      <Check className="w-3 h-3" />
                      Minst ett spesialtegn (!@#$%^&* osv.)
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 mb-2">Bekreft passord</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Skriv inn passordet igjen"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-3 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50"
                >
                  {loading ? 'Tilbakestiller...' : 'Tilbakestill passord'}
                </button>
              </form>
            </>
          )}

          {step === 'success' && (
            <>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl sm:text-3xl mb-2 bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent pb-1">
                  Passord tilbakestilt!
                </h2>
                <p className="text-gray-600 mb-6">
                  Ditt passord er nå oppdatert. Du kan nå logge inn på Talentfunn med ditt nye passord.
                </p>

                <button
                  onClick={onLoginRedirect}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all"
                >
                  Logg inn på Talentfunn
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
